import * as React from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import type { ComposeEmailOutput } from '@realadvisor/common-types';
import { CircleAlert, Mail, Trash2 } from 'lucide-react';
import prettyBytes from 'pretty-bytes';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { type ToolPart } from '@modules/_shared/components/chat/ApprovalActions';
import {
  type ComposeEmailResultHandler,
  type ComposeEmailSummaryLink,
  buildSentOutput,
  composeEmailCardView,
  composeEmailSummaryLinks,
  parseComposeEmailOutput,
} from '@modules/_shared/components/chat/composeEmailCard';
import { ConfirmationDialog } from '@modules/_shared/components/ConfirmationDialog';
import { ConnectEmailDialog } from '@modules/_shared/components/email-connect/ConnectEmailDialog';
import { CollapsibleAddressList } from '@modules/_shared/components/email-form/CollapsibleAddressList';
import { EmailForm } from '@modules/_shared/components/email-form/EmailForm';
import type {
  EmailFormRecipient,
  EmailFormSentDetails,
} from '@modules/_shared/components/email-form/types';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { UnfoldedEmailMessage } from '@modules/_shared/components/UnfoldedEmailMessage';
import { useComposeEmailDraft } from '@modules/_shared/hooks/useComposeEmailDraft';
import { useEmailConnectedRefresh } from '@modules/_shared/hooks/useEmailConnectedRefresh';
import { useHasValidNylasAccount } from '@modules/_shared/hooks/useHasValidNylasAccount';
import { CampaignStatusChip } from '@modules/emails/campaigns/CampaignStatusChip';
import { EMAIL_CAMPAIGN_PREVIEW } from '@modules/emails/campaigns/emailCampaignsQueries';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';
import { shortenFilename } from '@shared/utils/string';

// email_messages has no by_pk root field for CRM roles (select_aggregate only).
const COMPOSE_EMAIL_SENT_MESSAGE = gql(/* GraphQL */ `
  query ComposeEmailSentMessage($id: uuid!) {
    email_messages_aggregate(where: { id: { _eq: $id } }) {
      nodes {
        id
        from
        to
        cc
        bcc
        subject
        message_id
        created_by
        email_messages_files {
          nylas_file_id
          name
          size
          content_type
        }
      }
    }
  }
`);

// Card for the client-side `compose_email` tool: the agent's turn is paused
// until the user sends the drafted email from the real CRM composer, discards
// it, or dismisses a draft that could not be prepared. Only the user answers
// it (addToolResult via `onResult`); sendAutomaticallyWhen then resumes the
// agent, and the answered call folds into the agent's steps, where
// ComposeEmailSentView shows what was sent. Model text reaches the DOM only as
// React text or through the sanitised editor value built by
// useComposeEmailDraft.

type Colors = ReturnType<typeof useTheme>['colors'];

const CardShell = ({
  label,
  status,
  actions,
  children,
}: {
  label: string;
  status?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  const { colors } = useTheme();
  return (
    <div
      css={{
        border: `1px solid ${colors.divider}`,
        borderRadius: 8,
        backgroundColor: colors.white,
        overflow: 'hidden',
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 34,
          padding: '4px 10px',
          color: colors.grey700,
        }}
      >
        <Mail size={15} color={colors.grey600} style={{ flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, flex: 1, minWidth: 0 }}
          noWrap
        >
          {label}
        </Typography>
        {actions}
        {status}
      </div>
      {children != null && (
        <div css={{ padding: '0 8px 8px 8px' }}>{children}</div>
      )}
    </div>
  );
};

const Spinner = () => {
  const { colors } = useTheme();
  return <CircularProgress size={13} sx={{ color: colors.grey500 }} />;
};

const ErrorIcon = () => {
  const { colors } = useTheme();
  return <CircleAlert size={14} color={colors.errorText} />;
};

const DraftSkeleton = () => (
  <div>
    <Skeleton variant="text" width="70%" />
    <Skeleton variant="text" width="45%" />
    <Skeleton variant="rounded" height={64} sx={{ mt: 0.5 }} />
  </div>
);

const RawError = ({ text, colors }: { text: string; colors: Colors }) => (
  <pre
    css={{
      margin: 0,
      padding: '8px 10px',
      backgroundColor: colors.grey100,
      borderRadius: 6,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxHeight: 220,
      overflow: 'auto',
    }}
  >
    {text}
  </pre>
);

const ParentChips = ({ links }: { links: ComposeEmailSummaryLink[] }) =>
  links.length === 0 ? null : (
    <div css={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
      {links.map(link => (
        <Chip
          key={`${link.type}-${link.id}`}
          component={Link}
          to={link.path}
          clickable
          size="small"
          variant="outlined"
          label={link.label}
          sx={{ maxWidth: '100%' }}
        />
      ))}
    </div>
  );

const ComposeEmailWaiting = ({
  input,
  toolCallId,
  onResult,
  isMobile,
}: {
  input: unknown;
  toolCallId: string;
  onResult: ComposeEmailResultHandler;
  isMobile: boolean;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const draft = useComposeEmailDraft(input);
  const hasValidNylasAccount = useHasValidNylasAccount();
  const onEmailConnected = useEmailConnectedRefresh();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [connectOpen, setConnectOpen] = React.useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = React.useState(false);
  // Bumped by "Undo changes": remounting the form resets it to the draft.
  const [formRevision, setFormRevision] = React.useState(0);
  // A single EmailForm serves both the inline card and the full editor: it is
  // portalled into a detached host node that is moved between the two slots,
  // so switching never remounts it and the user's edits survive.
  const [formHost] = React.useState(() => document.createElement('div'));
  const [inlineSlot, setInlineSlot] = React.useState<HTMLDivElement | null>(
    null,
  );
  const [dialogSlot, setDialogSlot] = React.useState<HTMLDivElement | null>(
    null,
  );
  const formSlot = dialogOpen ? dialogSlot : inlineSlot;
  React.useLayoutEffect(() => {
    if (formSlot != null && formHost.parentNode !== formSlot) {
      formSlot.appendChild(formHost);
    }
  }, [formHost, formSlot]);
  // EmailForm calls onClose() right after onEmailSent: once answered, that
  // close (and any later click) must not report a second result.
  const answeredRef = React.useRef(false);
  const answer = (output: ComposeEmailOutput) => {
    if (answeredRef.current) {
      return;
    }
    answeredRef.current = true;
    onResult(toolCallId, output);
  };

  if (draft.status === 'loading') {
    return (
      <CardShell label={t('Drafting email…')} status={<Spinner />}>
        <DraftSkeleton />
      </CardShell>
    );
  }

  if (draft.status === 'error') {
    return (
      <CardShell
        label={t('Could not prepare the email')}
        status={<ErrorIcon />}
      >
        <RawError text={draft.error} colors={colors} />
        <div
          css={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}
        >
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => answer({ status: 'error', error: draft.error })}
          >
            {t('Dismiss')}
          </Button>
        </div>
      </CardShell>
    );
  }

  const isIndividual = draft.delivery === 'individual';
  const draftLabel = isIndividual ? t('Mass email draft') : t('Email draft');
  // The campaign logs each email on its recipient only, never on the parents.
  const parentLinks = isIndividual ? [] : draft.parentLinks;
  const discard = () => answer({ status: 'discarded' });
  const discardButton = (
    <IconButton
      size="small"
      title={t('Discard')}
      aria-label={t('Discard')}
      onClick={discard}
    >
      <Trash2 size={16} color={colors.grey600} />
    </IconButton>
  );

  if (!hasValidNylasAccount) {
    return (
      <CardShell label={draftLabel} actions={discardButton}>
        <ParentChips links={parentLinks} />
        <Typography variant="body2" sx={{ color: colors.grey700 }}>
          {t('Connect your mailbox to send this email.')}
        </Typography>
        <div
          css={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}
        >
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => setConnectOpen(true)}
          >
            {t('connectEmail')}
          </Button>
        </div>
        <ConnectEmailDialog
          open={connectOpen}
          onClose={() => setConnectOpen(false)}
          onSuccess={() => {
            setConnectOpen(false);
            onEmailConnected();
          }}
          onError={() => setConnectOpen(false)}
        />
      </CardShell>
    );
  }

  const handleSent = (
    response: unknown,
    sentRecipients: EmailFormRecipient[],
    sent?: EmailFormSentDetails,
  ) =>
    answer(
      buildSentOutput({
        response,
        sentRecipients,
        sent,
        draftSubject: draft.draftSubject,
        draftBodyPlaintext: draft.draftBodyPlaintext,
      }),
    );

  // EmailForm's own X / Cancel (Discard in the summary): collapse the full
  // editor back into the card, or confirm before discarding the draft from the
  // card.
  const closeForm = () => {
    if (answeredRef.current) {
      return;
    }
    if (dialogOpen) {
      setDialogOpen(false);
    } else {
      setConfirmDiscardOpen(true);
    }
  };

  return (
    <CardShell label={draftLabel}>
      <ParentChips links={parentLinks} />
      {dialogOpen && (
        <Typography variant="caption" sx={{ color: colors.grey600 }}>
          {t('The draft is open in the full editor.')}
        </Typography>
      )}
      <div ref={setInlineSlot} />
      <Dialog
        open={dialogOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogContent sx={{ p: 0 }}>
          <div ref={setDialogSlot} />
        </DialogContent>
      </Dialog>
      {createPortal(
        // The form is not a React child of the Dialog, so the Dialog's own
        // Escape handling never sees key presses from inside it.
        <div
          onKeyDown={event => {
            if (
              dialogOpen &&
              event.key === 'Escape' &&
              !event.defaultPrevented
            ) {
              closeForm();
            }
          }}
        >
          {/* No "add to templates" in either view: a failed template save
              keeps the form open for a retry after onEmailSent, but the
              answered card has already folded into the steps. */}
          <EmailForm
            key={formRevision}
            {...draft.formProps}
            variant={dialogOpen ? 'full' : 'summary'}
            onModify={() => setDialogOpen(true)}
            canSaveAsTemplate={false}
            surface={dialogOpen ? 'dialog' : 'paper'}
            onEmailSent={handleSent}
            onClose={closeForm}
            closeLabel={dialogOpen ? t('Back') : undefined}
            onRevert={
              dialogOpen
                ? () => setFormRevision(revision => revision + 1)
                : undefined
            }
          />
        </div>,
        formHost,
      )}
      <ConfirmationDialog
        open={confirmDiscardOpen}
        onClose={() => setConfirmDiscardOpen(false)}
        onConfirm={() => {
          setConfirmDiscardOpen(false);
          discard();
        }}
        title={t('Unsaved changes')}
        message={t('You have unsaved changes. Are you sure you want to close?')}
        confirmText={t('Discard')}
        destructive
      />
    </CardShell>
  );
};

const InactiveDraft = () => {
  const { t } = useLocale();
  const { colors } = useTheme();
  return (
    <CardShell label={t('Email draft')}>
      <Typography variant="caption" sx={{ color: colors.grey600 }}>
        {t('This email draft is no longer active.')}
      </Typography>
    </CardShell>
  );
};

const SentParentChips = ({ input }: { input: unknown }) => {
  const { t } = useLocale();
  const links = React.useMemo(
    () =>
      composeEmailSummaryLinks(input, {
        lead: t('Lead'),
        buyer_lead: t('Buyer lead'),
        enquiry: t('Enquiry'),
        lot: t('Listing'),
        contact: t('Contact'),
      }),
    [input, t],
  );
  return <ParentChips links={links} />;
};

type SentOutput = Extract<ComposeEmailOutput, { status: 'sent' }>;

const HeaderGrid = ({
  rows,
}: {
  rows: Array<{ label: string; value: React.ReactNode }>;
}) => {
  const { colors } = useTheme();
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        alignItems: 'center',
        columnGap: 8,
        rowGap: 2,
        fontSize: '0.75rem',
        lineHeight: 1.5,
        color: colors.grey800,
      }}
    >
      {rows.map(row => (
        <React.Fragment key={row.label}>
          <span css={{ color: colors.grey500 }}>{row.label}</span>
          <span css={{ wordBreak: 'break-word', minWidth: 0 }}>
            {row.value}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

// What the tool result stored: shown before the real email loads, and instead
// of it when it cannot be read.
const StoredSentSummary = ({ output }: { output: SentOutput }) => {
  const { t } = useLocale();
  return (
    <HeaderGrid
      rows={[
        {
          label: t('To'),
          value: <CollapsibleAddressList addresses={output.to} />,
        },
        ...(output.subject !== ''
          ? [{ label: t('Subject'), value: output.subject }]
          : []),
      ]}
    />
  );
};

const apolloErrorText = (error: { message: string }): string =>
  error.message !== '' ? error.message : JSON.stringify(error, null, 2);

// email_messages from/to/cc/bcc: jsonb arrays of `{ email, name }`.
const addressesOf = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.flatMap(item => {
        if (typeof item !== 'object' || item == null) {
          return [];
        }
        const { email, name } = item as { email?: unknown; name?: unknown };
        if (typeof email !== 'string' || email === '') {
          return [];
        }
        return [
          typeof name === 'string' && name.trim() !== '' && name !== email
            ? `${name.trim()} <${email}>`
            : email,
        ];
      })
    : [];

// `error` null: the row came back empty (not visible to this user).
const SentFallback = ({
  output,
  error,
}: {
  output: SentOutput;
  error: string | null;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  return (
    <>
      <StoredSentSummary output={output} />
      <div css={{ marginTop: 8 }}>
        {error != null ? (
          <RawError text={error} colors={colors} />
        ) : (
          <Typography variant="caption" sx={{ color: colors.grey600 }}>
            {t('The sent email could not be loaded.')}
          </Typography>
        )}
      </div>
    </>
  );
};

const SentLoading = ({ output }: { output: SentOutput }) => (
  <>
    <StoredSentSummary output={output} />
    <div css={{ marginTop: 8 }}>
      <Spinner />
    </div>
  </>
);

const SentEmailMessage = ({
  emailMessageId,
  output,
}: {
  emailMessageId: string;
  output: SentOutput;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const { data, loading, error } = useQuery(COMPOSE_EMAIL_SENT_MESSAGE, {
    variables: { id: emailMessageId },
  });
  if (error != null) {
    return <SentFallback output={output} error={apolloErrorText(error)} />;
  }
  const message = data?.email_messages_aggregate.nodes[0];
  if (message == null) {
    return loading ? (
      <SentLoading output={output} />
    ) : (
      <SentFallback output={output} error={null} />
    );
  }
  const cc = addressesOf(message.cc);
  const bcc = addressesOf(message.bcc);
  return (
    <>
      <HeaderGrid
        rows={[
          { label: t('From'), value: addressesOf(message.from).join(', ') },
          {
            label: t('To'),
            value: (
              <CollapsibleAddressList addresses={addressesOf(message.to)} />
            ),
          },
          ...(cc.length > 0
            ? [
                {
                  label: t('Cc'),
                  value: <CollapsibleAddressList addresses={cc} />,
                },
              ]
            : []),
          ...(bcc.length > 0
            ? [
                {
                  label: t('Bcc'),
                  value: <CollapsibleAddressList addresses={bcc} />,
                },
              ]
            : []),
          { label: t('Subject'), value: message.subject ?? t('No subject') },
        ]}
      />
      {message.message_id != null && message.created_by != null && (
        <div
          css={{
            marginTop: 8,
            padding: 8,
            borderRadius: 6,
            backgroundColor: colors.grey100,
            fontSize: '0.75rem',
            color: colors.grey800,
            maxHeight: 320,
            overflow: 'auto',
          }}
        >
          <UnfoldedEmailMessage
            emailMessageId={message.message_id}
            createdBy={message.created_by}
            fallback={<Spinner />}
          />
        </div>
      )}
      {message.email_messages_files.length > 0 && (
        <div css={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
          {message.email_messages_files.map((file, index) => (
            <Chip
              key={file.nylas_file_id ?? index}
              size="small"
              icon={<FileIcon mimetype={file.content_type ?? null} size={16} />}
              label={`${shortenFilename(file.name ?? '')} (${prettyBytes(
                file.size ?? 0,
              )})`}
            />
          ))}
        </div>
      )}
    </>
  );
};

const SentCampaign = ({
  campaignId,
  output,
}: {
  campaignId: string;
  output: SentOutput;
}) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(EMAIL_CAMPAIGN_PREVIEW, {
    variables: { id: campaignId },
    fetchPolicy: 'cache-and-network',
  });
  if (error != null) {
    return <SentFallback output={output} error={apolloErrorText(error)} />;
  }
  const campaign = data?.email_campaigns_by_pk;
  if (campaign == null) {
    return loading ? (
      <SentLoading output={output} />
    ) : (
      <SentFallback output={output} error={null} />
    );
  }
  const total = campaign.total.aggregate?.count ?? 0;
  const sent = campaign.sent.aggregate?.count ?? 0;
  const failed = campaign.error.aggregate?.count ?? 0;
  return (
    <>
      <HeaderGrid
        rows={[
          {
            label: t('Subject'),
            value:
              campaign.first_message[0]?.subject_template ?? output.subject,
          },
          {
            label: t('Status'),
            value: <CampaignStatusChip status={campaign.status} />,
          },
          { label: t('sent'), value: `${sent} / ${total}` },
          ...(failed > 0 ? [{ label: t('Error'), value: String(failed) }] : []),
        ]}
      />
      <div css={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button
          size="small"
          variant="outlined"
          component={Link}
          to={`/emails/campaigns/${campaign.id}`}
        >
          {t('View')}
        </Button>
      </div>
    </>
  );
};

// Expanded body of an answered compose_email step. Mounted only while the step
// is expanded, so the email / campaign is fetched on demand.
export const ComposeEmailSentView = ({ part }: { part: ToolPart }) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  if (part.state === 'output-error') {
    return <RawError text={part.errorText ?? t('Failed')} colors={colors} />;
  }
  const output = parseComposeEmailOutput(part.output);
  if (output == null) {
    return typeof part.output === 'string' && part.output !== '' ? (
      <RawError text={part.output} colors={colors} />
    ) : (
      <Typography variant="caption" sx={{ color: colors.grey600 }}>
        {t('This email draft is no longer active.')}
      </Typography>
    );
  }
  switch (output.status) {
    case 'discarded':
      return (
        <Typography variant="caption" sx={{ color: colors.grey600 }}>
          {t('Email discarded')}
        </Typography>
      );
    case 'error':
      return <RawError text={output.error} colors={colors} />;
    case 'sent':
      return (
        <>
          <SentParentChips input={part.input} />
          {output.campaignId != null ? (
            <SentCampaign campaignId={output.campaignId} output={output} />
          ) : output.emailMessageId != null ? (
            <SentEmailMessage
              emailMessageId={output.emailMessageId}
              output={output}
            />
          ) : (
            <StoredSentSummary output={output} />
          )}
        </>
      );
  }
};

export const ComposeEmailToolCard = ({
  part,
  onResult,
  isMobile = false,
}: {
  part: ToolPart;
  // Only passed for the chat's last turn, the one that can still be answered.
  onResult?: ComposeEmailResultHandler;
  isMobile?: boolean;
}) => {
  const { t } = useLocale();
  const toolCallId = part.toolCallId;
  const view = composeEmailCardView(
    part.state,
    onResult != null && toolCallId != null,
  );

  switch (view) {
    case 'drafting':
      return (
        <CardShell label={t('Drafting email…')} status={<Spinner />}>
          <DraftSkeleton />
        </CardShell>
      );
    case 'waiting':
      return onResult != null && toolCallId != null ? (
        <ComposeEmailWaiting
          input={part.input}
          toolCallId={toolCallId}
          onResult={onResult}
          isMobile={isMobile}
        />
      ) : null;
    case 'inactive':
      return <InactiveDraft />;
    case 'answered':
      return (
        <CardShell label={t('Email draft')}>
          <ComposeEmailSentView part={part} />
        </CardShell>
      );
  }
};
